import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import TableFilter from "src/components/TableFilter";
import ControlService from "src/modules/data-protection/apis/ControlService";

const FilteringComponent = ({
    t,
    lng,
    onChange
}) => {
    const [ tableFilters, setTableFilters ] = useState({
        text: undefined,
        status: undefined,
        sectionId: undefined,
        hasTasks: undefined
    });

    const {
        data: sections,
        isFetching: sectionsAreFetching
    } = useQuery({
        queryKey: [
            "data-protection-controls-sections-list",
            lng
        ],
        queryFn: async () => {
            const service = ControlService.getInstance();
            return await service.fetchSections();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("Failed to load the list of control sections."), {
                type: "error"
            });
        }
    });

    const statuses = useMemo(() => {
        return [
            { value: "NEW", label: t("New") },
            { value: "IN_PROGRESS", label: t("In Progress") },
            { value: "REVIEW", label: t("Review") },
            { value: "CLOSED", label: t("Closed") },
            { value: "DELETED", label: t("Deleted") },
        ];
    }, [t]);

    const tableFilterTabs = useMemo(() => {
        return [
            {
                type: 'check_list',
                name: 'status',
                title: 'Task Status',
                icon: <i className="ri-checkbox-circle-line" />,
                isLoading: false,
                defaultValue: tableFilters?.status,
                data: (statuses || []).map((item) => ({
                    ...item,
                    id: item.value
                })),
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return t(item.label) 
                }
            },
            {
                type: 'radio_list',
                name: 'sectionId',
                title: 'Section',
                icon: <i className="ri-list-unordered" />,
                isLoading: sectionsAreFetching,
                defaultValue: tableFilters?.sectionId,
                data: sections || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.title
                }
            },
            {
                type: 'radio_list',
                name: 'hasTasks',
                title: 'Has Tasks',
                icon: <i className="ri-checkbox-circle-line" />,
                isLoading: false,
                defaultValue: tableFilters?.hasTasks,
                data: [
                    {
                        id: true,
                        title: 'Yes'
                    },
                    {
                        id: false,
                        title: 'No'
                    }
                ],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.title
                }
            },
            {
                type: 'search_textbox',
                name: 'text',
                title: 'Search',
                icon: <i className="ri-terminal-box-line" />,
                defaultValue: tableFilters?.text,
                options: {
                    placeholder: t('Search by title or section'),
                    typingDelay: 750
                },
                displayIndependently: true,
                onFilterChange: (value) => {
                    setTableFilters((currentState) => ({...currentState, text: value}))
                }
            }
        ]
    }, [
        tableFilters,
        statuses,
        sectionsAreFetching,
        sections,
        t
    ]);

    const onTableFilterOkButtonClicked = (filters, independentTabsData) => {
        if (filters) {
            const result = {}
            if ('status' in filters) {
                result.status = filters.status !== 'all' ? filters.status : undefined
            }

            if ('sectionId' in filters) {
                result.sectionId = filters.sectionId !== 'all' ? filters.sectionId : undefined
            }

            if ('hasTasks' in filters) {
                result.hasTasks = filters.hasTasks !== 'all' ? filters.hasTasks : undefined
            }

            setTableFilters({
                ...tableFilters,
                ...result
            })
        } else {
            setTableFilters({
                text: independentTabsData?.text,
                status: undefined,
                sectionId: undefined,
                hasTasks: undefined
            })
        }
    }

    useEffect(() => {
        onChange && onChange(tableFilters)
    }, [ 
        tableFilters,
        onChange
    ]);

    return (
        <TableFilter
            className="me-2"
            defaultActiveTab='status'
            tabs={tableFilterTabs}
            onOkButtonClicked={
                onTableFilterOkButtonClicked
            }
        />
    )
}

export default withNamespaces()(FilteringComponent);